import type { CSSInterpolation } from '@emotion/serialize';
import React, { use, useState } from 'react';

import Close from 'src/components/icons/close';
import ExpandIcon from 'src/components/icons/expand';
import TickIcon from 'src/components/icons/tick';
import AccessToClubsClubList from 'src/components/online-joining/your-membership/rate-cards/other-club-access-popup/access-to-clubs-club-list';
import type { AccessToClubDetailsTree } from 'src/components/online-joining/your-membership/rate-cards/other-club-access-popup/access-to-clubs-helper';
import {
  countAccessibleClubs,
  countInaccessibleClubs,
} from 'src/components/online-joining/your-membership/rate-cards/other-club-access-popup/access-to-clubs-helper';
import { SiteContext } from 'src/context/site-context';
import type { Styling } from 'src/context/styling';
import { flex } from 'src/styling/constants';
import mq from 'src/styling/media-queries';

type AccessToClubsProps = {
  clubsTree: AccessToClubDetailsTree;
};
type AccessToClubsInternalProps = AccessToClubsProps & {
  currentDepth: number;
};

const depthSpacing = '0.75rem';
const depthBorder = '1px';

const clubListContainerStyling = (depth: number, styling: Styling): CSSInterpolation => ({
  width: '100%',
  paddingLeft: depth > 0 ? depthSpacing : 0,
  borderLeft: depth > 0 ? `${depthBorder} solid ${styling.colors.border}` : 0,
});

const groupRowsContainerStyling = (
  depth: number,
  isGroupOpen: boolean,
  styling: Styling
): CSSInterpolation => ({
  width: '100%',
  paddingLeft: depth > 0 ? depthSpacing : 0,
  borderLeft: depth > 0 ? `${depthBorder} solid ${styling.colors.border}` : 0,
  paddingBottom: isGroupOpen ? '1rem' : 0,
});

const groupRowStyling: CSSInterpolation = [flex.rowSpacedOut];

const groupLabelStyling = (depth: number): CSSInterpolation => ({
  flex: 1,
  marginLeft: `calc(-1 * (${depth} * ${depthSpacing} + ${depth} * ${depthBorder}))`,
});

const groupLabelTextStyling = (depth: number): CSSInterpolation => ({
  paddingLeft: `calc(${depth} * ${depthSpacing} + ${depth} * ${depthBorder})`,
});

const groupRowIconSideStyling: CSSInterpolation = [
  flex.row,
  { flex: 1 },
  mq.withSmallTablet({ justifyContent: ['flex-end', 'space-between'] }),
];

const iconColumnStyling: CSSInterpolation = [
  flex.row,
  {
    width: '2rem',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
];

const expandIconStyling: CSSInterpolation = [iconColumnStyling, { flex: 1 }];

const countContainerStyling: CSSInterpolation = [flex.row, iconColumnStyling];

const canBeAccessedCountContainerStyling: CSSInterpolation = [
  countContainerStyling,
  { flex: 1 },
  mq.withSmallTablet({ marginRight: [null, '1rem', '1.5rem'] }),
];

const AccessToClubsInternal = ({ clubsTree, currentDepth }: AccessToClubsInternalProps) => {
  const { styling } = use(SiteContext);
  const [expandedGroupKey, setExpandedGroupKey] = useState('');

  const handleClickGroup = (groupKey: string) => {
    if (groupKey === expandedGroupKey) {
      setExpandedGroupKey('');
    } else {
      setExpandedGroupKey(groupKey);
    }
  };

  return clubsTree.type === 'clubs' ? (
    <div css={clubListContainerStyling(currentDepth, styling)}>
      <AccessToClubsClubList
        clubs={clubsTree.clubs}
        useTwoColumnsOnLargerScreen={clubsTree.clubs.length > 4}
      />
    </div>
  ) : (
    <>
      {Array.from(clubsTree.groups.entries()).map(([groupKey, groupValue]) => {
        const isGroupExpanded = expandedGroupKey === groupKey;
        const groupDisplayName = groupValue.displayName ?? groupKey;
        return (
          <div
            key={groupKey}
            css={groupRowsContainerStyling(currentDepth, isGroupExpanded, styling)}
          >
            <div css={groupRowStyling} onClick={(_) => handleClickGroup(groupKey)}>
              <div css={groupLabelStyling(currentDepth)}>
                <div css={groupLabelTextStyling(currentDepth)}>{groupDisplayName}</div>
              </div>
              <div css={groupRowIconSideStyling}>
                <div css={[flex.row, { justifyContent: 'space-between' }]}>
                  <div css={canBeAccessedCountContainerStyling}>
                    <div css={{ color: styling.colors.successGreen }}>
                      {countAccessibleClubs(groupValue)}
                    </div>
                    <TickIcon size="1em" fill={styling.colors.successGreen} />
                  </div>
                  <div css={countContainerStyling}>
                    <div css={{ color: styling.colors.paleRed }}>
                      {countInaccessibleClubs(groupValue)}
                    </div>
                    <Close size="1em" fill={styling.colors.paleRed} />
                  </div>
                </div>
                <div css={expandIconStyling}>
                  <ExpandIcon
                    size={'1rem'}
                    fill={styling.colors.bodyText}
                    isExpanded={isGroupExpanded}
                  />
                </div>
              </div>
            </div>
            <div css={!isGroupExpanded && { visibility: 'hidden', height: 0, overflow: 'hidden' }}>
              <AccessToClubsInternal clubsTree={groupValue} currentDepth={currentDepth + 1} />
            </div>
          </div>
        );
      })}
    </>
  );
};

const AccessToClubs = (props: AccessToClubsProps) => (
  <AccessToClubsInternal {...props} currentDepth={0} />
);

export default AccessToClubs;
